import { useEffect, useState } from 'react';

import {
  evaluateSpreadJSAsync,
  type SpreadJSEngineResult,
} from '@/lib/engine/spreadjs-engine-client';
import { logError } from '@/lib/logger';
import { downloadSheetSpecAsExcelSpreadJS } from '@/lib/utils/spreadjs-excel-export';

import { MetadataTab } from './tabs/MetadataTab';
import { OutputTab } from './tabs/OutputTab';
import { ValidationTab } from './tabs/ValidationTab';

interface EvalResult {
  id: string;
  prompt_id: string;
  model_config_id: string;
  is_valid_json: boolean;
  error?: string;
  execution_time_ms?: number;
  created_at: string;
  prompt?: any;
  model_config?: any;
  evaluation_ok?: boolean;
  formula_errors?: boolean;
  stages?: any;
  last_stage?: string;
  stage_errors?: any;
  engine_checks?: any;
  sheet_json?: any;
  raw_output?: string;
  compute_values?: any;
  token_usage?: any;
}

type Tab = 'output' | 'metadata' | 'validation';

export function ExecutionDetailModal({
  result,
  onClose,
  loading = false,
}: {
  result: EvalResult;
  onClose: () => void;
  loading?: boolean;
}) {
  const [activeTab, setActiveTab] = useState<Tab>('output');
  const [spreadjsResult, setSpreadjsResult] =
    useState<SpreadJSEngineResult | null>(null);
  const [spreadjsEvaluating, setSpreadjsEvaluating] = useState(false);

  // Evaluate with SpreadJS in the browser
  useEffect(() => {
    if (result.is_valid_json && result.sheet_json) {
      // Use async IIFE to avoid synchronous setState in effect body
      (async () => {
        setSpreadjsEvaluating(true);
        try {
          const evalResult = await evaluateSpreadJSAsync(result.sheet_json);
          setSpreadjsResult(evalResult);
        } catch (err) {
          logError('SpreadJS evaluation error', err);
          setSpreadjsResult({
            computedValues: [],
            errorCells: [],
            errors: [err instanceof Error ? err.message : String(err)],
            detailedErrors: [],
          });
        } finally {
          setSpreadjsEvaluating(false);
        }
      })();
    }
  }, [result.sheet_json, result.is_valid_json]);

  const handleDownload = async () => {
    if (!result.sheet_json) {
      alert('No spreadsheet data available to download');
      return;
    }

    try {
      const filename =
        `${result.prompt?.name || 'spreadsheet'}_${result.model_config?.name || 'output'}`.replace(
          /[^a-zA-Z0-9_-]/g,
          '_'
        );
      await downloadSheetSpecAsExcelSpreadJS(
        result.sheet_json,
        filename + '.xlsx'
      );
    } catch (error: any) {
      alert(`Failed to download: ${error.message}`);
    }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'output', label: 'Output' },
    { id: 'metadata', label: 'Metadata' },
    { id: 'validation', label: 'Validation' },
  ];

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '2rem',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          maxWidth: '1200px',
          width: '100%',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{ padding: '1.5rem 2rem', borderBottom: '1px solid #E8E3DD' }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'start',
            }}
          >
            <div>
              <h2
                style={{
                  fontSize: '1.5rem',
                  fontWeight: 600,
                  color: '#47331F',
                  margin: 0,
                }}
              >
                Execution Details
              </h2>
              <p
                style={{
                  color: '#8B7764',
                  marginTop: '0.5rem',
                  fontSize: '0.9375rem',
                }}
              >
                {result.prompt?.name && `${result.prompt.name} × `}
                {(result as any).config_name ||
                  result.model_config?.name ||
                  'Unknown Model'}
              </p>
              <p
                style={{
                  color: '#8B7764',
                  marginTop: '-10px',
                  fontSize: '0.75rem',
                }}
              >
                Result ID: {result.id}
              </p>
            </div>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              {result.sheet_json && (
                <button
                  onClick={handleDownload}
                  style={{
                    padding: '0.5rem 1rem',
                    background: '#B7343A',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'background 0.15s ease',
                  }}
                  onMouseEnter={e =>
                    (e.currentTarget.style.background = '#9A2B30')
                  }
                  onMouseLeave={e =>
                    (e.currentTarget.style.background = '#B7343A')
                  }
                >
                  Download Excel
                </button>
              )}
              <button
                onClick={onClose}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '2rem',
                  cursor: 'pointer',
                  color: '#A8988A',
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.5rem' }}>
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: '0.625rem 1.25rem',
                  border: 'none',
                  background: activeTab === tab.id ? '#B7343A' : 'transparent',
                  color: activeTab === tab.id ? 'white' : '#8B7764',
                  borderRadius: '6px',
                  fontSize: '0.9375rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '2rem' }}>
          {loading && (
            <div
              style={{ textAlign: 'center', padding: '3rem', color: '#8B7764' }}
            >
              Loading result data...
            </div>
          )}

          {!loading && activeTab === 'output' && (
            <OutputTab
              rawOutput={result.raw_output}
              isValidJson={result.is_valid_json}
              sheetJson={result.sheet_json}
              computeValues={result.compute_values}
              error={result.error}
            />
          )}

          {activeTab === 'metadata' && (
            <MetadataTab
              evalPrompt={result.prompt}
              modelConfig={result.model_config}
              executionTimeMs={result.execution_time_ms}
              tokenUsage={result.token_usage}
              createdAt={result.created_at}
            />
          )}

          {!loading && activeTab === 'validation' && (
            <ValidationTab
              spreadjsResult={spreadjsResult}
              spreadjsEvaluating={spreadjsEvaluating}
              hyperformulaResult={result.engine_checks?.hyperformula}
              sheetJson={result.sheet_json}
              isValidJson={result.is_valid_json}
              stages={result.stages}
              lastStage={result.last_stage}
              stageErrors={result.stage_errors}
              error={result.error}
            />
          )}
        </div>
      </div>
    </div>
  );
}
